'use client';

import { useErrorBoundary } from '@/app/contexts/ErrorBoundaryContext';

import { useCallback } from 'react';

interface UseGlobalErrorHandlerOptions {
  showGlobalError?: boolean;
  customErrorMessage?: string;
  isCritical?: boolean;
}

/**
 * Hook para manejo global de errores con opciones configurables. Permite controlar si los errores se muestran globalmente y su criticidad.
 *
 * @param {UseGlobalErrorHandlerOptions} options - Opciones de configuración del manejador de errores.
 *
 * @returns {Object} Objeto con funciones para manejar errores síncronos y asíncronos.
 */
export const useGlobalErrorHandler = (options: UseGlobalErrorHandlerOptions = {}) => {
  const { showError } = useErrorBoundary();
  const { showGlobalError = true, customErrorMessage, isCritical = true } = options;

  const handleError = useCallback(
    (error: unknown, context?: string) => {
      console.error(`Error in ${context || 'unknown context'}:`, error);

      if (showGlobalError && isCritical) {
        const errorMessage =
          customErrorMessage ||
          (error instanceof Error ? error.message : 'Ha ocurrido un error inesperado');

        showError(error instanceof Error ? error : new Error(String(error)), errorMessage);
      }
    },
    [showError, showGlobalError, customErrorMessage, isCritical]
  );

  const handleAsyncError = useCallback(
    async <T>(asyncFn: () => Promise<T>, context?: string): Promise<T | null> => {
      try {
        return await asyncFn();
      } catch (error) {
        handleError(error, context);
        return null;
      }
    },
    [handleError]
  );

  return {
    handleError,
    handleAsyncError
  };
};
